import React from "react";
import { Link } from "react-router-dom";
import LegalPage, { P, Section, List, Mail } from "../../components/LegalPage";
import {
  COMPANY,
  SUPPORT_EMAIL,
  PRIVACY_UPDATED,
  MINIMUM_AGE,
} from "../../data/legalInfo";

export default function Privacy() {
  return (
    <LegalPage title="Privacy Policy" subtitle={`Last updated ${PRIVACY_UPDATED}`}>
      <P>
        Lineup is a fantasy college football game operated by {COMPANY}. This policy explains what
        we collect, why we collect it, and what you can do about it. It covers the Lineup website
        and the Lineup mobile app.
      </P>

      <Section title="What we collect">
        <P>Information you give us when you create an account and play:</P>
        <List
          items={[
            "Your first and last name, which other managers in your leagues can see.",
            `Your email address, used to sign you in, verify your account, and reset your password.`,
            `Your date of birth, used only to confirm you are at least ${MINIMUM_AGE}. We store the date so we do not have to ask again.`,
            "Your password, which is hashed by our authentication provider. We never see or store it in readable form.",
            "Optional profile details you choose to add, such as a profile image and your league smack talk.",
            "Your gameplay: the leagues you belong to, your draft picks, weekly lineups, roster moves, and points.",
            "Your daily game results: which game you played, the day, and your score. We use the totals to see which games people play.",
            "Your Pick Three entries: the three teams and the captain you choose each week. We use them to score your week and to tell you how you placed against other players, as a share of players only. Other players never see your picks, your name, or your score. Teams you choose before signing up are kept only on your device until you make an account. If you delete your account, your entries stay only as anonymous records and stop counting toward anyone's placing.",
            "Your email address, if you ask to hear when next season opens. It is held only once you confirm it from the email we send, it is used for that announcement and nothing else, and every email carries an unsubscribe link.",
            "Reports you file about another manager's content, and the managers you block. A report includes what you wrote and a copy of the content you reported.",
          ]}
        />
        <P>Information collected automatically:</P>
        <List
          items={[
            "A push notification token, if you turn on notifications. It identifies your device to Apple or Google so we can deliver league alerts. You can revoke it at any time in your device settings.",
            "Standard server logs kept by our hosting providers, including IP address and request timestamps, used for security and troubleshooting.",
          ]}
        />
      </Section>

      <Section title="What we do not collect">
        <P>
          Lineup contains no advertising SDKs, no third-party analytics, and no cross-app tracking.
          We do not collect your location, your contacts, your photos beyond a profile image you
          select, or any payment information. Lineup is free to play and does not process payments.
        </P>
      </Section>

      <Section title="How we use it">
        <List
          items={[
            "To run your account and keep you signed in.",
            "To operate your leagues: drafts, lineups, scoring, standings, and roster moves.",
            "To send you service email such as account verification and password resets.",
            "To send push notifications you have opted into, such as draft and lineup reminders.",
            "To investigate problems, prevent abuse, and keep the service secure.",
          ]}
        />
        <P>
          We do not sell your information, and we do not share it with advertisers.
        </P>
      </Section>

      <Section title="Who else handles your data">
        <P>
          We use a small number of service providers to run Lineup. They process data on our behalf
          and are not permitted to use it for their own purposes.
        </P>
        <List
          items={[
            "Supabase: database, authentication, and account email delivery.",
            "Vercel: hosting for the website.",
            "Render: hosting for the background service that ingests scores and updates standings.",
            "Apple Push Notification service: delivery of push notifications to iOS devices.",
            "Firebase Cloud Messaging (Google): delivery of push notifications to Android devices. We use it only to deliver notifications. It does not receive your account details or league data.",
          ]}
        />
        <P>
          Lineup also reads public college football schedules and scores from the College Football
          Data API and from ESPN. That traffic is outbound only. No information about you is sent to
          either provider.
        </P>
        <P>
          We may disclose information if we are legally required to, or where it is necessary to
          protect the rights and safety of our users.
        </P>
      </Section>

      <Section title="What other people can see">
        <P>
          Leagues are private and joined by invite code. Managers in a league you belong to can see
          your name, your profile image, your rosters and lineups, your roster moves, and your
          points. Anyone with the league invite code can join that league, so share it only with
          people you want in your league.
        </P>
      </Section>

      <Section title="How long we keep it">
        <P>
          We keep your account information while your account is open. When you delete your account,
          we remove your personal information from our active systems immediately and from encrypted
          backups within 30 days. League records may retain an anonymized placeholder where you
          previously appeared, so that the remaining managers' league history and standings stay
          intact.
        </P>
        <P>
          The full detail of what is erased and what is left behind is set out on the{" "}
          <Link to="/delete-account" style={{ color: "var(--color-brand)", textDecoration: "none" }}>
            delete your account
          </Link>{" "}
          page.
        </P>
      </Section>

      <Section title="Your choices">
        <List
          items={[
            "Access and correction: view and edit your profile details from your account settings.",
            <>
              Deletion: delete your account from your account settings at any time, or from the{" "}
              <Link to="/delete-account" style={{ color: "var(--color-brand)", textDecoration: "none" }}>
                delete your account
              </Link>{" "}
              page on the web. Deletion is permanent.
            </>,
            "Notifications: turn push notifications off in your device settings.",
            "Email: account and security email is required while your account is open. We do not send marketing email.",
          ]}
        />
        <P>
          You can also reach us at <Mail address={SUPPORT_EMAIL} /> with any request about your data,
          and we will respond within 30 days.
        </P>
      </Section>

      <Section title="Security">
        <P>
          All traffic between the app and our servers is encrypted in transit. Database access is
          restricted by row level security, so an account can only read the leagues and rosters it
          belongs to. No system is perfectly secure, and we cannot guarantee absolute security, but
          we will notify affected users promptly if a breach involving personal information occurs.
        </P>
      </Section>

      <Section title="Age requirement">
        <P>
          Lineup is for people aged {MINIMUM_AGE} and over. We do not knowingly collect information
          from anyone under {MINIMUM_AGE}. If you believe a minor has created an account, contact us
          at <Mail address={SUPPORT_EMAIL} /> and we will delete it.
        </P>
      </Section>

      <Section title="Where your data is held">
        <P>
          Lineup is operated from the United States, and your information is stored and processed
          there. If you use Lineup from outside the United States, you are sending your information
          to the United States.
        </P>
      </Section>

      <Section title="Changes to this policy">
        <P>
          If we change this policy in a way that affects how we handle your information, we will
          update the date at the top of this page and notify you in the app before the change takes
          effect.
        </P>
      </Section>

      <Section title="Contact">
        <P>
          Questions about this policy: <Mail address={SUPPORT_EMAIL} />.
        </P>
      </Section>
    </LegalPage>
  );
}
